import { NOT_ACTIVE_TASK_STATUSSES } from 'constants/tasks';

import { createSlice } from '@reduxjs/toolkit';
import type { UpdateTaskCompleteParams } from 'components/modals/MarkCompleteAsync/markCompleteAsync.types';
import { SubmitPABody } from 'components/modals/SubmitPA/submitPA.types';
import { AppointmentStatus } from 'enums/appointmentStatus';
import { TaskCategories } from 'enums/taskCategories';
import { PARequestFlow } from 'enums/taskDetailsStatus';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import type {
  CallQualityInfo,
  IntakeItemsInfo,
  ReminderProps,
  TaskDetailsProps,
  TaskProps,
  TasksQueryParams,
  TasksSummaryProps
} from 'models/tasks.types';
import socketStaff from 'socket/socketStaff';
import type { RootState } from 'store';
import {
  isAvsSelectionBlocking,
  selectionCompletionSettled,
  selectionCompletionStarted
} from 'store/avs/avsSelectionSlice';
import { HistoryProps } from 'store/history/history.types';
import { DefaultQueryParams } from 'store/patients/patients.types';
import { ObjectValues } from 'utils/common/types';
import { readSchedulingRecoveryDetail, readSchedulingRecoveryList } from 'utils/schedulingRecovery';

import { apiSlice } from '../api/apiSlice';
import {
  ApptAsyncScheduleDetails,
  AssignTaskToTeamRequestProps,
  AsyncScheduleDetails,
  AvailableStaffForPatientProps,
  AvailableStaffProps,
  AvailableStaffResponse,
  BulkEditTaskParams,
  BulkEditTaskResponse,
  ChangeDispositionRequestProps,
  ChangePAStatusProps,
  CreateNewTaskParams,
  CreateReminderResponse,
  CreateRequestOrderTaskParams,
  DiagnosisCodesProps,
  EscalateTaskRequestProps,
  initialStateTask,
  initialStateTasks,
  RequestPAProps,
  ReviewRejectedLabRequest,
  SubmitPAFormRequestProps,
  TaskRequestOrderReasonsType,
  TaskRequestProps,
  TaskStatisticsResponse,
  TaskTagsType,
  UpdatedTaskResponseProps
} from './task.types';

export const taskSlice = createSlice({
  initialState: initialStateTask,
  name: 'task',
  reducers: {
    resetTask: () => initialStateTask,
    setIntakeItems: (state, action) => {
      state.intakeItems = [...action.payload].sort(
        (currItems, prevItems) => currItems.sortNum - prevItems.sortNum
      );
    },
    setSelectRequireAppointment: (state, action) => {
      state.selectRequireAppointment = action.payload;
    },
    setTaskDetails: (state, action) => {
      state.taskDetails = action.payload || initialStateTask.taskDetails;
    },

    setTaskDetailsDocuments: (state, action) => {
      state.taskDetails.patientDocuments = action.payload || [];
    }
  }
});

export const {
  resetTask,
  setIntakeItems,
  setSelectRequireAppointment,
  setTaskDetails,
  setTaskDetailsDocuments
} = taskSlice.actions;

export const tasksApi = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    assignProvider: build.mutation({
      invalidatesTags: (_result, _error, arg) => [{ id: arg.patientId, type: 'Patient' }],
      query: ({
        appointmentId,
        providerId
      }: {
        appointmentId: string;
        patientId: string;
        providerId: string;
      }) => ({
        body: { userId: providerId },
        method: 'PATCH',
        url: `/tasks/${appointmentId}/assign-provider`
      })
    }),
    assignStaffToTask: build.mutation<
      { message: string },
      { staffId: string; taskId: string; timezone?: string }
    >({
      invalidatesTags: ['Task'],
      query: ({ staffId, taskId, timezone }) => ({
        body: { timezone: timezone, userId: staffId },
        method: 'PATCH',
        url: `/tasks/${taskId}/assign-staff`
      })
    }),
    assignTaskToTeam: build.mutation<{ message: string }, AssignTaskToTeamRequestProps>({
      invalidatesTags: ['Task'],
      query: ({ taskId, ...body }) => ({
        body,
        method: 'PATCH',
        url: `/tasks/${taskId}/assign-teams`
      })
    }),
    changeDisposition: build.mutation<{ message: string }, ChangeDispositionRequestProps>({
      invalidatesTags: ['Task'],
      query: ({ body, taskId }) => ({
        body,
        method: 'PATCH',
        url: `/tasks/${taskId}/change-disposition`
      })
    }),
    createCallQuality: build.mutation({
      query: ({
        callQualityInfo,
        taskId
      }: {
        callQualityInfo?: CallQualityInfo;
        taskId: string;
      }) => ({
        body: callQualityInfo,
        method: 'POST',
        url: `/tasks/${taskId}/call-quality`
      })
    }),
    createNewTask: build.mutation<{ data: { id: string } }, { body: CreateNewTaskParams }>({
      query: ({ body }) => ({
        body,
        method: 'POST',
        url: '/tasks/request'
      })
    }),
    createReminder: build.mutation<CreateReminderResponse, ReminderProps>({
      query: (body) => ({
        body,
        method: 'POST',
        url: `/tasks/reminder`
      })
    }),
    createRequestOrderTask: build.mutation<
      { data: { id: string } },
      { body: CreateRequestOrderTaskParams }
    >({
      query: ({ body }) => ({
        body,
        method: 'POST',
        url: '/tasks/request-order'
      })
    }),
    deescalateTask: build.mutation<{ message: string }, EscalateTaskRequestProps>({
      invalidatesTags: ['Task'],
      query: ({ note, taskId }) => ({
        body: { note },
        method: 'PATCH',
        url: `tasks/${taskId}/deescalate`
      })
    }),
    escalateTask: build.mutation<{ message: string }, EscalateTaskRequestProps>({
      invalidatesTags: ['Task'],
      query: ({ taskId, ...body }) => ({
        body,
        method: 'PATCH',
        url: `tasks/${taskId}/escalate`
      })
    }),

    getActionLog: build.query<HistoryProps, { params: DefaultQueryParams; taskId: string }>({
      async onCacheEntryAdded(_, { cacheDataLoaded, cacheEntryRemoved, dispatch }) {
        try {
          await cacheDataLoaded;
          const terminalRefreshSubscription = dispatch(
            tasksApi.endpoints.watchTaskTerminalUpdates.initiate()
          );
          await cacheEntryRemoved;
          terminalRefreshSubscription.unsubscribe();
        } catch {
          // no-op when the action-log cache is removed before it loads
        }
      },
      providesTags: (_result, _error, { taskId }) => [{ id: `HISTORY:${taskId}`, type: 'Task' }],
      query: ({ params, taskId }) => ({
        params,
        url: `/tasks/${taskId}/action-logs`
      })
    }),
    getApptAsyncScheduleDetails: build.query<{ data: ApptAsyncScheduleDetails }, void>({
      query: () => ({
        url: `/appt-async/schedule-details`
      })
    }),
    getAsyncScheduleDetails: build.query<{ data: AsyncScheduleDetails }, void>({
      query: () => ({
        url: `/async/schedule-details`
      })
    }),
    getAvailableStaff: build.query<
      { data: AvailableStaffResponse[] },
      { params: AvailableStaffProps; taskId: string }
    >({
      query: ({ params, taskId }) => ({
        params,
        url: `/tasks/${taskId}/available-staff`
      })
    }),
    getAvailableStaffForPatient: build.query<
      { data: AvailableStaffResponse[] },
      { params: AvailableStaffForPatientProps; patientId: string }
    >({
      query: ({ params, patientId }) => ({
        params,
        url: `/patients/${patientId}/available-staff`
      })
    }),
    getDiagnosesDetails: build.query<{ codes: DiagnosisCodesProps[] }, void>({
      query: () => ({
        method: 'GET',
        url: `/diagnosis-codes`
      }),
      transformResponse: (response: { data: { codes: DiagnosisCodesProps[] } }) => response.data
    }),
    getMessageTopics: build.query<string[], void>({
      query: () => ({
        method: 'GET',
        url: `/task-topics`
      }),
      transformResponse: (response: { data: string[] }) => response.data
    }),
    getPreFillPAForm: build.query<{ data: SubmitPABody }, { taskId: string }>({
      query: ({ taskId }) => ({
        method: 'GET',
        url: `/tasks/${taskId}/prefill-pa-form`
      })
    }),
    getTaskDetails: build.query<
      TaskDetailsProps,
      { params?: { autoAssign?: boolean }; taskId: string }
    >({
      async onCacheEntryAdded(
        _,
        { cacheDataLoaded, cacheEntryRemoved, dispatch, getState, updateCachedData }
      ) {
        let listenerRegistered = false;
        const updateTask = async (data: UpdatedTaskResponseProps) => {
          if (isEmpty(data.updatedData)) {
            const state = getState() as RootState;
            const taskId = state.task.taskDetails._id;
            if (taskId !== data.taskId) return;

            const result = await dispatch(
              tasksApi.endpoints.getTaskDetails.initiate({ taskId }, { forceRefetch: true })
            );
            const currentTaskId = (getState() as RootState).task.taskDetails._id;
            if (currentTaskId !== data.taskId) return;

            if ('data' in result && result.data) {
              updateCachedData((draft) => {
                Object.assign(draft, result.data);
              });
              dispatch(setTaskDetails(result.data));
              dispatch(tasksApi.util.invalidateTags([{ id: taskId, type: 'Task' }]));
            }
            return;
          }

          updateCachedData((draft) => {
            const state = getState() as RootState;
            const taskId = state.task.taskDetails._id;

            /**
             * Determines if the current draft's `_id` matches both the provided `data.taskId` from socket and `taskId` from state.
             * It needs in case where the previous task was completed/closed, a new one was opened, but socket data arrived before the new task data was received.
             * */
            const taskIndex = draft?._id === data.taskId && draft?._id === taskId;
            if (taskIndex) {
              const updatedTask = {
                ...cloneDeep(draft),
                ...data.updatedData,
                ...(data.updatedData.appointmentInfo && {
                  appointmentInfo: {
                    ...draft.appointmentInfo,
                    ...data.updatedData.appointmentInfo
                  }
                })
              };
              Object.assign(draft, updatedTask);
              dispatch(setTaskDetails(updatedTask));
              if (data.updatedData.intakeItemsInfo) {
                dispatch(setIntakeItems(data.updatedData.intakeItemsInfo));
              }
            }
          });
        };

        try {
          await cacheDataLoaded;
          socketStaff.on('taskUpdatedData', updateTask);
          listenerRegistered = true;
          await cacheEntryRemoved;
        } catch {
          // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
          // in which case `cacheDataLoaded` will throw
        } finally {
          if (listenerRegistered) {
            socketStaff.off('taskUpdatedData', updateTask);
          }
        }
      },
      providesTags: (_result, _error, arg) =>
        !arg.params?.autoAssign ? [{ id: arg.taskId, type: 'Task' }] : [],
      query: ({ params, taskId }) => ({
        method: 'GET',
        params: params,
        url: `/tasks/${taskId}`
      }),
      transformResponse: (response: { data: TaskDetailsProps }) => ({
        ...response.data,
        schedulingRecovery: readSchedulingRecoveryDetail(response.data.schedulingRecovery)
      })
    }),
    getTaskFiltersDetails: build.query<{ futureTasksRange: string }, void>({
      query: () => ({
        method: 'GET',
        url: `/task-filters`
      }),
      transformResponse: (response: { data: { futureTasksRange: string } }) => response.data
    }),
    getTaskRequestOrderReasons: build.query<TaskRequestOrderReasonsType[], void>({
      query: () => ({
        method: 'GET',
        url: `/tasks/request/reasons`
      }),
      transformResponse: (response: { data: TaskRequestOrderReasonsType[] }) => response.data
    }),
    getTasks: build.query<TaskRequestProps, { params: TasksQueryParams }>({
      keepUnusedDataFor: 30,
      async onCacheEntryAdded(
        params,
        { cacheDataLoaded, cacheEntryRemoved, dispatch, updateCachedData }
      ) {
        let listenersRegistered = false;
        let unsubscribeTerminalRefresh: (() => void) | undefined;
        const updateTaskInList = (data: UpdatedTaskResponseProps) => {
          if (isEmpty(data.updatedData)) return;

          const updatedStatus = data.updatedData.status;
          const isTerminalUpdate =
            typeof updatedStatus === 'string' && NOT_ACTIVE_TASK_STATUSSES.includes(updatedStatus);

          updateCachedData((draft) => {
            if (!draft?.tasks?.length) return;
            const taskIndex = draft?.tasks.findIndex((task: TaskProps) => task._id === data.taskId);
            if (Number.isInteger(taskIndex) && taskIndex !== -1) {
              const updatedTasks: TaskProps[] = cloneDeep(draft.tasks);
              const isCancelledOnboarding =
                updatedTasks[taskIndex].category === TaskCategories.Onboarding &&
                updatedStatus?.toLowerCase() === AppointmentStatus.Cancelled;

              // NOTE: We have a tasks list with snoozed tasks on Patient page.
              // We want to have all tasks there even completed ones.
              // So we do not remove completed tasks from this list.
              // But we want to remove completed tasks from other lists.
              const isListWithSnoozesTasks = params?.params?.enableSnoozedTasks;
              const isCompletedList = params?.params?.completed === true;
              const isHistoryList = params?.params?.category?.includes('History');
              const isActiveList = !isCompletedList && !isHistoryList && !isListWithSnoozesTasks;

              // Remove task from list or update it
              if (isActiveList && (isTerminalUpdate || isCancelledOnboarding)) {
                updatedTasks.splice(taskIndex, 1);
                draft.totalCount = Math.max(0, draft.totalCount - 1);
              } else {
                updatedTasks[taskIndex] = {
                  ...updatedTasks[taskIndex],
                  ...data.updatedData
                };
              }
              draft.tasks = updatedTasks;
            }
          });
        };

        const updateTaskDetails = (event: { details: { note: string }; tasksIdList: string[] }) => {
          updateCachedData((draft) => {
            if (!draft?.tasks?.length) return;
            event.tasksIdList.forEach((id) => {
              draft.tasks.forEach((task) => {
                if (task._id === id) {
                  task.details.note = event.details.note;
                }
              });
            });
          });
        };

        try {
          await cacheDataLoaded;
          const terminalRefreshSubscription = dispatch(
            tasksApi.endpoints.watchTaskTerminalUpdates.initiate()
          );
          unsubscribeTerminalRefresh = () => terminalRefreshSubscription.unsubscribe();
          socketStaff.on('taskDetailUpdate', updateTaskDetails);
          socketStaff.on('taskUpdatedData', updateTaskInList);
          listenersRegistered = true;
          await cacheEntryRemoved;
        } catch {
          // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
          // in which case `cacheDataLoaded` will throw
        } finally {
          if (listenersRegistered) {
            socketStaff.off('taskDetailUpdate', updateTaskDetails);
            socketStaff.off('taskUpdatedData', updateTaskInList);
          }
          unsubscribeTerminalRefresh?.();
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.tasks.map(({ _id }) => ({ id: _id, type: 'Task' as const })),
              { id: 'LIST', type: 'Task' }
            ]
          : [{ id: 'LIST', type: 'Task' }],
      query: ({ params }) => {
        let queryParams = params;

        if (params?.category?.includes('History')) {
          queryParams = {
            ...params,
            completed: true
          };
        }

        if (params?.startCreateDate && !params?.endCreateDate) {
          queryParams = {
            ...params,
            createdAt: params.startCreateDate
          };
          delete queryParams.startCreateDate;
        }

        return {
          params: queryParams,
          url: '/tasks'
        };
      },
      transformResponse: (response: {
        data: TaskProps[];
        info: {
          isSteadyMDTeamMember?: boolean;
          summary?: TasksSummaryProps;
          totalCount: number;
        };
      }) => ({
        isSteadyMDTeamMember: response.info.isSteadyMDTeamMember,
        summary: response.info.summary,
        tasks: response.data.map((task) => ({
          ...task,
          schedulingRecovery: readSchedulingRecoveryList(task.schedulingRecovery)
        })),
        totalCount: response.info.totalCount
      })
    }),
    getTasksSummary: build.query<
      {
        isSteadyMDTeamMember?: boolean;
        summary: TasksSummaryProps;
      },
      { params: TasksQueryParams }
    >({
      keepUnusedDataFor: 15 * 60,
      async onCacheEntryAdded(_, { cacheDataLoaded, cacheEntryRemoved, dispatch }) {
        try {
          await cacheDataLoaded;
          const terminalRefreshSubscription = dispatch(
            tasksApi.endpoints.watchTaskTerminalUpdates.initiate()
          );
          await cacheEntryRemoved;
          terminalRefreshSubscription.unsubscribe();
        } catch {
          // no-op when the summary cache is removed before it loads
        }
      },
      providesTags: [{ id: 'SUMMARY', type: 'Task' }],
      query: ({ params }) => ({
        params,
        url: '/tasks-summary'
      }),
      transformResponse: (response: {
        data: {
          isSteadyMDTeamMember?: boolean;
          summary: TasksSummaryProps;
        };
      }) => response.data
    }),
    getTaskStatistics: build.query<
      TaskStatisticsResponse,
      { params: { audience?: string; endDate?: string; startDate?: string } }
    >({
      keepUnusedDataFor: 60,
      providesTags: [{ id: 'STATISTICS', type: 'Task' }],
      query: ({ params }) => ({
        params,
        url: '/admin/tasks/statistics'
      }),
      transformResponse: (response: { data: TaskStatisticsResponse }) => response.data
    }),
    getTaskTags: build.query<TaskTagsType[], void>({
      query: () => ({
        method: 'GET',
        url: `/tasks/tags`
      }),
      transformResponse: (response: { data: TaskTagsType[] }) => response.data
    }),
    markPARequestSent: build.mutation<{ message: string }, { taskId: string }>({
      query: ({ taskId }) => ({
        body: { taskId },
        method: 'PATCH',
        url: `/tasks/${taskId}/mark-pa-request-sent`
      }),
      transformResponse: (response: { data: { message: string } }) => response.data
    }),
    moveQueueTaskToQueue: build.mutation<{ message: string }, { appointmentId: string }>({
      invalidatesTags: ['Task'],
      query: ({ appointmentId }) => ({
        method: 'POST',
        url: `/appointments/${appointmentId}/move-to-queue`
      }),
      transformResponse: (response: { data: { message: string } }) => response.data
    }),
    pauseTask: build.mutation<
      { message: string },
      { note: string; resumedAt: string; taskId: string }
    >({
      query: ({ note, resumedAt, taskId }) => ({
        body: {
          pausedDetails: {
            note,
            resumedAt
          }
        },
        method: 'PATCH',
        url: `/tasks/${taskId}/pause`
      })
    }),
    removeBillingBlock: build.mutation({
      invalidatesTags: ['Patient'],
      query: (patientId: string) => ({
        method: 'POST',
        url: `/patients/${patientId}/remove-payment-block`
      })
    }),
    requestPA: build.mutation<
      {
        data: {
          id: string;
        };
        message: string;
      },
      RequestPAProps
    >({
      invalidatesTags: ['Benefits'],
      query: (body) => ({
        body,
        method: 'POST',
        url: `/tasks/request-pa`
      })
    }),
    reviewRejectedLab: build.mutation<ResponseType, ReviewRejectedLabRequest>({
      query: ({ action, orderId, reason, taskId }) => ({
        body: {
          action,
          reason,
          taskId
        },
        method: 'POST',
        url: `/orders/${orderId}/review-rejected-lab`
      })
    }),
    setPAFlow: build.mutation<
      { message: string },
      { body: { paRequestFlow: ObjectValues<typeof PARequestFlow> }; taskId: string }
    >({
      query: ({ body, taskId }) => ({
        body,
        method: 'PATCH',
        url: `/tasks/${taskId}/set-pa-flow`
      })
    }),
    setPAStatus: build.mutation<{ message: string }, ChangePAStatusProps>({
      invalidatesTags: ['Benefits'],
      query: (body) => ({
        body,
        method: 'POST',
        url: `/tasks/set-pa-status`
      })
    }),
    signOffOnLab: build.mutation({
      invalidatesTags: ['Task'],
      query: ({ taskId }: { taskId: string }) => ({
        method: 'POST',
        url: `/tasks/${taskId}/sign-off-lab`
      })
    }),
    stopReceivingTasks: build.mutation({
      invalidatesTags: ['Task'],
      query: (patientId: string) => ({
        method: 'POST',
        url: `/patients/${patientId}/stop-receiving-tasks`
      })
    }),
    submitPAForm: build.mutation<{ message: string }, SubmitPAFormRequestProps>({
      invalidatesTags: ['Benefits'],
      query: ({ body, taskId }) => ({
        body,
        method: 'POST',
        url: `/tasks/${taskId}/submit-develop-health-pa`
      })
    }),
    tasksBulkUpdate: build.mutation<BulkEditTaskResponse, BulkEditTaskParams>({
      invalidatesTags: ['Task'],
      query: (body) => ({
        body,
        method: 'PATCH',
        url: `/tasks-bulk-update`
      }),
      transformResponse: (response: BulkEditTaskResponse) => response
    }),
    unpauseTask: build.mutation<{ message: string }, { taskId: string }>({
      query: ({ taskId }) => ({
        method: 'PATCH',
        url: `/tasks/${taskId}/unpause`
      })
    }),
    updateIntakeItems: build.mutation({
      async onQueryStarted({ taskId, ...intakeItems }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          tasksApi.util.updateQueryData('getTaskDetails', { taskId }, (draft) => {
            Object.assign(draft, intakeItems);
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      query: ({ intakeItems, taskId }: { intakeItems: IntakeItemsInfo[]; taskId: string }) => ({
        body: { intakeItems },
        method: 'POST',
        url: `/tasks/${taskId}/update-intake-items`
      })
    }),
    updateSendTaskToPhysician: build.mutation({
      invalidatesTags: ['Task'],
      query: ({ body, taskId }: { body?: { markUrgent: boolean }; taskId: string }) => ({
        body,
        method: 'PATCH',
        url: `/tasks/${taskId}/send-to-physician`
      }),
      transformResponse: (response: { message: string }) => response
    }),
    updateTaskAssign: build.mutation({
      query: ({
        audience,
        releaseTask,
        taskId
      }: {
        audience?: string[];
        releaseTask?: boolean;
        taskId: string;
      }) => ({
        body: { ...(releaseTask ? { releaseTask } : { audience }) },
        method: 'PATCH',
        url: `/tasks/${taskId}/transfer`
      })
    }),
    updateTaskComplete: build.mutation<{ message: string }, UpdateTaskCompleteParams>({
      invalidatesTags: ['RespondState', 'Task'],
      queryFn: async ({ taskId, ...body }, api, _options, baseQuery) => {
        if (isAvsSelectionBlocking(api.getState() as RootState, taskId)) {
          return {
            error: {
              data: {
                message: 'Resolve the prescription selection before completing this encounter.'
              },
              status: 409
            }
          };
        }
        const locks = [`task:${taskId}`];
        api.dispatch(selectionCompletionStarted(locks));
        try {
          const result = await baseQuery({
            body,
            method: 'PATCH',
            url: `/tasks/${taskId}/mark-complete`
          });
          return result.error
            ? { error: result.error }
            : { data: result.data as { message: string } };
        } finally {
          const edits = Object.values((api.getState() as RootState).avsSelection.edits).filter(
            (edit) => edit.taskId === taskId
          );
          api.dispatch(
            apiSlice.util.invalidateTags(
              edits.map((edit) => ({
                id: `${edit.patientId}:${edit.taskId}`,
                type: 'AvsPrescriptionSelection'
              }))
            )
          );
          api.dispatch(selectionCompletionSettled(locks));
        }
      }
    }),
    updateTaskFiltersDetails: build.mutation<
      { message: string },
      { body: { futureTasksRange?: string } }
    >({
      query: ({ body }) => ({
        body,
        method: 'PUT',
        url: `/task-filters`
      }),
      transformResponse: (response: { message: string }) => response
    }),
    updateTaskRequestToTakeOver: build.mutation<
      { message: string },
      string | { expectedRevision: number; taskId: string }
    >({
      query: (arg) => {
        const taskId = typeof arg === 'string' ? arg : arg.taskId;
        return {
          ...(typeof arg === 'string' ? {} : { body: { expectedRevision: arg.expectedRevision } }),
          method: 'PATCH',
          url: `/tasks/${taskId}/takeover`
        };
      },
      transformResponse: (response: { message: string }) => response
    }),
    watchTaskTerminalUpdates: build.query<null, void>({
      keepUnusedDataFor: 0,
      async onCacheEntryAdded(_, { cacheDataLoaded, cacheEntryRemoved, dispatch }) {
        let listenerRegistered = false;
        const refreshTerminalTaskCaches = (data: UpdatedTaskResponseProps) => {
          const updatedStatus = data.updatedData.status;
          const isTerminalUpdate =
            typeof updatedStatus === 'string' && NOT_ACTIVE_TASK_STATUSSES.includes(updatedStatus);
          if (!isTerminalUpdate) return;

          dispatch(
            tasksApi.util.invalidateTags([
              { id: 'SUMMARY', type: 'Task' },
              { id: `HISTORY:${data.taskId}`, type: 'Task' }
            ])
          );
        };

        try {
          await cacheDataLoaded;
          socketStaff.on('taskUpdatedData', refreshTerminalTaskCaches);
          listenerRegistered = true;
          await cacheEntryRemoved;
        } catch {
          // no-op when the watcher cache is removed before it loads
        } finally {
          if (listenerRegistered) {
            socketStaff.off('taskUpdatedData', refreshTerminalTaskCaches);
          }
        }
      },
      queryFn: () => ({ data: null })
    })
  })
});

export const tasksSlice = createSlice({
  initialState: initialStateTasks,
  name: 'tasks',
  reducers: {
    resetTasks: () => initialStateTasks,
    setTasksSummary: (state, action) => {
      state.tasksSummary = action.payload.summary;
    }
  }
});

export const { resetTasks, setTasksSummary } = tasksSlice.actions;

export const selectTask = (state: RootState) => state.task;
export const selectTasks = (state: RootState) => state.tasks;

export const {
  useAssignProviderMutation,
  useAssignStaffToTaskMutation,
  useAssignTaskToTeamMutation,
  useChangeDispositionMutation,
  useCreateCallQualityMutation,
  useCreateNewTaskMutation,
  useCreateReminderMutation,
  useCreateRequestOrderTaskMutation,
  useDeescalateTaskMutation,
  useEscalateTaskMutation,
  useGetActionLogQuery,
  useGetDiagnosesDetailsQuery,
  useGetMessageTopicsQuery,
  useGetPreFillPAFormQuery,
  useGetTaskDetailsQuery,
  useGetTaskRequestOrderReasonsQuery,
  useGetTasksSummaryQuery,
  useGetTaskTagsQuery,
  useLazyGetActionLogQuery,
  useLazyGetApptAsyncScheduleDetailsQuery,
  useLazyGetAsyncScheduleDetailsQuery,
  useLazyGetAvailableStaffForPatientQuery,
  useLazyGetAvailableStaffQuery,
  useLazyGetTaskDetailsQuery,
  useLazyGetTaskFiltersDetailsQuery,
  useLazyGetTasksQuery,
  useLazyGetTasksSummaryQuery,
  useLazyGetTaskStatisticsQuery,
  useMarkPARequestSentMutation,
  useMoveQueueTaskToQueueMutation,
  usePauseTaskMutation,
  useRemoveBillingBlockMutation,
  useRequestPAMutation,
  useReviewRejectedLabMutation,
  useSetPAFlowMutation,
  useSetPAStatusMutation,
  useSignOffOnLabMutation,
  useStopReceivingTasksMutation,
  useSubmitPAFormMutation,
  useTasksBulkUpdateMutation,
  useUnpauseTaskMutation,
  useUpdateIntakeItemsMutation,
  useUpdateSendTaskToPhysicianMutation,
  useUpdateTaskAssignMutation,
  useUpdateTaskCompleteMutation,
  useUpdateTaskFiltersDetailsMutation,
  useUpdateTaskRequestToTakeOverMutation
} = tasksApi;
